import {
  OVERLAY_BACKGROUND_COLOR,
  PORTRAIT_IMAGE_MASK
} from '@/common/constants';
import { getDistance, ptToPx } from '@/common/utils';

const getRNumber = isImage => {
  return isImage ? 7 : 18;
};

const getFillColor = isImage => {
  return isImage ? 'white' : '#58595B';
};

const getFillStyle = showOverlay => {
  return showOverlay.value > 0 ? showOverlay.color : OVERLAY_BACKGROUND_COLOR;
};

const getWidthHeightForObject = (
  widthForImage,
  heightForImage,
  widthForText,
  heightForText,
  isImage
) => {
  const w = isImage ? widthForImage : widthForText;
  const h = isImage ? heightForImage : heightForText;
  return {
    w,
    h
  };
};

/**
 * To draw a rounded box onto an element
 * @param {Object} ctx context canvas
 * @param {Object} boundingRect bounding box of objects
 * @param {Number} centerX
 * @param {Number} centerY
 * @param {Boolean} showOverlay
 * @param {Boolean} isImage
 */
const drawRoundedRect = function({
  ctx,
  boundingRect,
  centerX,
  centerY,
  showOverlay,
  cornerRounding = 0,
  mask,
  isImage,
  angle
}) {
  if (!showOverlay?.isDisplayed) return;
  let { top, left, width, height } = boundingRect;
  const widthForText = 114;
  const heightForText = 60;

  const widthForImage = 40;
  const heightForImage = 20;

  const { w, h } = getWidthHeightForObject(
    widthForImage,
    heightForImage,
    widthForText,
    heightForText,
    isImage
  );

  ctx.save();

  // render overlay background
  ctx.fillStyle = getFillStyle(showOverlay);

  if (angle) {
    ctx.save();
    // this translate and rotate is used for drawing overlay background
    ctx.translate(left, top);
    ctx.rotate(angle);
    ctx.translate(-left, -top);
    ctx.fillRect(left, top, width, height);
    ctx.restore();

    // this translate and rotate is used for drawing rounded box and a number inside it
    ctx.translate(centerX, centerY);
    ctx.rotate(angle);
    ctx.translate(-centerX, -centerY);
  } else {
    if (!isImage) {
      ctx.fillRect(left, top, width, height);
    } else {
      const d = Math.min(width, height);
      const adjustedCornerRounding = ptToPx(cornerRounding);
      let r = adjustedCornerRounding;
      ctx.save();
      ctx.beginPath();
      if (mask === PORTRAIT_IMAGE_MASK.CIRCLE) {
        ctx.ellipse(0, 0, width / 2, height / 2, 0, 0, 2 * Math.PI);
      } else {
        r = Math.min(r, d / 2);
        r = Math.max(r, 0);
        ctx.moveTo(left + r, top);
        ctx.arcTo(left + width, top, left + width, top + height, r);
        ctx.arcTo(left + width, top + height, left, top + height, r);
        ctx.arcTo(left, top + height, left, top, r);
        ctx.arcTo(left, top, left + width, top, r);
      }
      ctx.closePath();
      ctx.clip();
      ctx.fillRect(left, top, width, height);
      ctx.restore();
    }
  }

  const x = centerX - w / 2;
  const y = centerY - h / 2;
  // rNumber: radius value is randomly choosen to make the corner looks good (this r render background of number)
  const rNumber = getRNumber(isImage);

  ctx.beginPath();
  ctx.moveTo(x + rNumber, y);
  ctx.arcTo(x + w, y, x + w, y + h, rNumber);
  ctx.arcTo(x + w, y + h, x, y + h, rNumber);
  ctx.arcTo(x, y + h, x, y, rNumber);
  ctx.arcTo(x, y, x + w, y, rNumber);
  ctx.closePath();
  ctx.clip();

  ctx.fillStyle = getFillColor(isImage);
  ctx.fillRect(centerX - w / 2, centerY - h / 2, w, h);

  ctx.restore();
};

export const handleRenderOverlayText = function(ctx) {
  if (!this?.showOverlay?.isDisplayed) return;

  const [tl, tr, br] = this.getCoords(true);

  const width = getDistance(tl, tr);
  const height = getDistance(tr, br);

  const { x: centerX, y: centerY } = this.getCenterPoint();
  const boundingRect = { left: tl.x, top: tl.y, width, height };
  const angle = (Math.PI * (this.angle % 360)) / 180;

  drawRoundedRect({
    ctx,
    boundingRect,
    centerX,
    centerY,
    showOverlay: this.showOverlay,
    isImage: false,
    angle
  });

  ctx.save();

  if (angle) {
    ctx.translate(centerX, centerY);
    ctx.rotate(angle);
    ctx.translate(-centerX, -centerY);
  }

  ctx.fillStyle = 'white';
  ctx.font = '35px "MuseoSans 300"';
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  ctx.fillText(`${this.showOverlay.value}`, centerX, centerY);
  ctx.restore();
};

export const handleRenderOverlayImage = function(ctx) {
  if (!this?.showOverlay?.isDisplayed) return;

  // only image has scale value, so we need to take it into account
  // the context of image object has been modified so can not get corner points
  // we cannot use getBoudingRect() when object is rotated
  // so we have to calc its dimensions based on conner positions
  const [tl, tr, br] = this.getCoords();

  const width = getDistance(tl, tr);
  const height = getDistance(tr, br);

  const left = -width / 2;
  const top = -height / 2;

  // Reset scale and skew values of transform matix
  const { e, f } = ctx.getTransform();
  ctx.setTransform(1, 0, 0, 1, e, f);

  const boundingRect = { left, top, width, height };

  const isImage = true;

  const centerX = 0;
  const centerY = 0;

  const rateDiffBoundingAndObject = width / this.width;

  drawRoundedRect({
    ctx,
    boundingRect,
    centerX,
    centerY,
    showOverlay: this.showOverlay,
    cornerRounding: this.cornerRounding * rateDiffBoundingAndObject,
    mask: this.mask,
    isImage
  });

  ctx.save();
  ctx.fillStyle = 'black';
  ctx.font = `${11}px "MuseoSans 300"`;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  ctx.fillText(`${this.showOverlay.value}`, centerX, centerY);
  ctx.restore();
};
